/**
 * Centralized logging utility
 * In production, only errors are logged
 * In development, all logs are shown
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  private formatMessage(level: LogLevel, message: string, data?: unknown): LogEntry {
    return {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isDevelopment) {
      return true; // Log everything in development
    }
    // In production, only log warnings and errors
    return level === 'warn' || level === 'error';
  }

  private log(level: LogLevel, message: string, data?: unknown): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry = this.formatMessage(level, message, data);

    // In production, you might want to send to an error tracking service
    if (this.isProduction && level === 'error') {
      // TODO: Send to error tracking service (e.g., Sentry, LogRocket)
      // Example: Sentry.captureException(new Error(message), { extra: data });
    }

    // Use appropriate console method
    switch (level) {
      case 'debug':
        console.debug(`[DEBUG] ${entry.timestamp}`, message, data || '');
        break;
      case 'info':
        console.info(`[INFO] ${entry.timestamp}`, message, data || '');
        break;
      case 'warn':
        console.warn(`[WARN] ${entry.timestamp}`, message, data || '');
        break;
      case 'error':
        console.error(`[ERROR] ${entry.timestamp}`, message, data || '');
        break;
    }
  }

  debug(message: string, data?: unknown): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: unknown): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown): void {
    this.log('warn', message, data);
  }

  error(message: string, data?: unknown): void {
    this.log('error', message, data);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for use in other files
export type { LogLevel };

